/**
 * Tonist audio engine — public surface.
 *
 *   import { getEngine } from '@/engine';
 *   const engine = getEngine();                 // singleton, no AudioContext until start()
 *   await engine.start();                       // from a user gesture: creates/unlocks the context, loads worklets
 *   await engine.loadDrone(drone);              // decodes samples, renders multisample roots, builds the FX bus
 *   const v = engine.noteOn('sa', 138.6, { radius: 0.5, velocity: 0 });
 *   v.setPitch(146.8, glideSeconds(1)); v.setGesture({ radius: 0.8, velocity: 300 }); v.latch(true); v.release();
 *   engine.setContinuous('sa', hz, gesture) / engine.stopContinuous('sa')   // Continuous mode
 *   engine.setTonicHz(hz)                       // implied tanpura follows the tonic
 *   engine.getVoiceWaveform(v.id, f32x256); engine.getMasterSpectrum(u8x64)  // read inside a rAF loop
 *   engine.updateDrone(drone)                   // live editor edits (optional method on AudioEngine)
 *   engine.samples                              // SampleRegistry: list/get/refresh/buffer/registerLocal/prepareUserBuffer
 *
 * Meters and loading state are published to `engineStore` (src/store/engineStore.ts) at ~30 Hz.
 *
 * Module map: context.ts (AudioContext lifecycle) · registry.ts (samples) · sampler.ts (multisample roots via
 * signalsmith-stretch offline) · sampledUnit.ts / freezeUnit.ts / synth/units.ts (sources) · filter.ts ·
 * modulation.ts (LFOs, ticker) · voice.ts · droneGraph.ts + fx/* (delay, reverb, shimmer) · tanpura.ts · engine.ts.
 * Worklets live in public/worklets (granular.js, pitchshift.js, signalsmith-stretch.js).
 */
import { Engine } from './engine';
import type { AudioEngine } from './types';

export type { AudioEngine, VoiceHandle, SampleRegistry, GestureState, EngineState, PreparedUserBuffer } from './types';
export { Engine, MAX_VOICES } from './engine';
export { macroMap, type MacroMap } from './params';
export { selectRoots, chooseRoot } from './sampler';

let instance: Engine | null = null;

/** The process-wide engine. Creating it allocates nothing audible; call `start()` from a user gesture. */
export function getEngine(): AudioEngine {
  if (!instance) instance = new Engine();
  return instance;
}
